import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TouchableWithoutFeedback,
} from 'react-native';
import Utility from '../../utils/Utility';
import AudioHandler from '../../utils/AudioHandler';
import VerticalMediaImage from './VerticalMediaImage';
import VerticalMediaBgImage from './VerticalMediaBgImage';
import LinearGradient from 'react-native-linear-gradient';
import {
  EventType,
  AnalyticsManager,
  AnalyticsUtilty,
  EventParameterKey,
} from '../../analytics';
import { debounce, capitalize } from 'lodash';
import Card from '../../lib/Card';
import MediaComponent from './MediaComponent';
import {
  MAX_LINES,
  ContentPlatforms,
  VIDEO_DISTRIBUTION_URL,
  VIDEO_CDN_URL,
  ROUTINE_ACTIVITY_TYPES,
} from '../../config/Constants';
import withNavigation from '../../utils/WithNavigation';
import MediaCardStyles from './styles';
import images from '../../theme/Images';
import ScaleAnimate from '../shared/ScaleAnimate';
import FastImageView from '../FastImageView';
import ProductRatingAndMoreInfo from '../Product/ProductRatingAndMoreInfo';
import { getMinifiedImage } from '../../utils/ImageUtility';
import colors from '../../theme/Colors';
import { playlistStyle, verticalPlaylistCardHeight } from './Style';
import { isDesktop } from '../../utils/BooleanUtility';

const SquareMediaCard = React.memo((props) => {
  const {
    item = {},
    goToNextPage,
    index,
    iteratorIndex,
    cardWidth,
    extraEventParameters = {},
    previousScreen = '',
    navigation,
    onRef,
    listData: { options: { edge_to_edge_playlist = '' } } = {},
    onItemPress = () => {},
    routineActivityId = '',
    totalNumberOfVideos,
  } = props;

  const {
    artist: { name: artistName = '', id: artistId } = {},
    entity: { name: entityName = '', id: entityId, type: entityType } = {},
    metadata: {
      contentPlatform = 'foxy',
      title: contentTitle,
      id: videoId,
      duration,
    } = {},
  } = item;

  const [shouldPlay, setShouldPlay] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [muted, setMuted] = useState(AudioHandler.isMuted);
  const [screenInFocus, setScreenInFocus] = useState(true);

  const videoMetadata = {
    [EventParameterKey.VIDEO_ID]: videoId,
    [EventParameterKey.VIDEO_TITLE]: contentTitle,
    [EventParameterKey.VIDEO_DURATION]: duration,
    [EventParameterKey.VIDEO_TYPE]: capitalize(contentPlatform),
    [EventParameterKey.ENTITY_TYPE]: entityType,
    [EventParameterKey.ENTITY_NAME]: entityName,
    [EventParameterKey.ENTITY_ID]: entityId,
    [EventParameterKey.SOURCE]: 'vertical_card',
    [EventParameterKey.SCREEN_NAME]: previousScreen,
    ...extraEventParameters,
  };

  const isCurrentIndex = index === iteratorIndex;

  const imageWidth = (item.metadata && item.metadata.width) || 560;
  const imageHeight = (item.metadata && item.metadata.height) || 375;

  const mediaHeight = (imageHeight / imageWidth) * cardWidth;
  const mediaStyle = {
    width: '100%',
    height: mediaHeight,
  };
  const showBgImage = verticalPlaylistCardHeight > mediaHeight + 10;
  const isCardTappedForFirstTime = useRef(false);
  const currentTime = useRef(0);
  const clickParamValue = 'full_screen';

  const debouncedFullScreenButton = useCallback(
    debounce(() => {
      AnalyticsManager.logEvent(EventType.verticalVideoCard.VERTICAL_VIDEO_CARD, {
        ...videoMetadata,
        [EventParameterKey.CLICK]: clickParamValue,
      });
      navigateToMedia();
    }, 3000, { leading: true, trailing: false }),
    [videoMetadata]
  );

  const debouncedCard = useCallback(
    debounce(() => {
      cardTapped();
    }, 3000, { leading: true, trailing: false }),
    []
  );

  useEffect(() => {
    if (onRef) {
      onRef({ pause, play });
    }

    const focusListener = navigation.addListener('focus', () => {
      setScreenInFocus(true);
    });

    const didBlurListener = navigation.addListener('blur', () => {
      setScreenInFocus(false);
    });

    return () => {
      focusListener();
      didBlurListener();
    };
  }, [navigation, onRef]);

  const toggleMutedState = useCallback(() => {
    if (!isCurrentIndex) return;

    if (AudioHandler.isMuted) {
      AudioHandler.changeAudioSessionCategoryToAmbient();
    } else {
      AudioHandler.changeAudioSessionCategoryToPlayBack();
    }
    AudioHandler.isMuted = !AudioHandler.isMuted;
    setMuted((prev) => !prev);
  }, [isCurrentIndex]);

  const onNextButtonTapped = useCallback(() => {
    const isLastVideo = iteratorIndex === totalNumberOfVideos - 1;
    if (isLastVideo) {
      pause();
      currentTime.current = 0;
    }
    goToNextPage();
  }, [iteratorIndex, totalNumberOfVideos, goToNextPage]);

  const navigateToMedia = useCallback(() => {
    const itemData = item;
    if (Utility.isPresent(itemData)) {
      AnalyticsUtilty.fireItemClickEvent(
        previousScreen,
        itemData?.metadata?.id,
        itemData?.type,
        itemData?.metadata?.title,
        iteratorIndex,
        props.id,
        props.listData?.display,
        props.listName,
        props.listIndex,
        '',
        '',
        props.listContent,
        '',
        itemData?.slug,
        props.listData?.slug,
        extraEventParameters,
      );
    }
    navigation.push('ContentModal', {
      itemData,
      listId: props.id,
      index: iteratorIndex,
      listData: props.listData,
      seekToTimeOnLoad: currentTime.current,
      previousScreen,
    });
  }, [item, iteratorIndex, navigation, previousScreen, props, extraEventParameters]);

  const cardTapped = useCallback(() => {
    const { metadata: { contentPlatform } = {} } = item;
    const clickParamValue = 'card';
    isCardTappedForFirstTime.current = true;
    AnalyticsManager.logEvent(EventType.verticalVideoCard.VERTICAL_VIDEO_CARD, {
      ...videoMetadata,
      [EventParameterKey.CLICK]: clickParamValue,
    });
    onItemPress(routineActivityId, ROUTINE_ACTIVITY_TYPES.VIDEO);
    navigateToMedia();
  }, [item, videoMetadata, onItemPress, routineActivityId, navigateToMedia]);

  const pause = useCallback(() => {
    setShouldPlay(false);
  }, []);

  const play = useCallback(() => {
    setShouldPlay(true);
  }, []);

  const setCurrentTime = useCallback((time) => {
    currentTime.current = time;
  }, []);

  const renderMedia = useCallback(() => {
    if (!shouldPlay) {
      return null;
    }

    const {
      image_url: imageUrl,
      metadata: { contentPlatform, videoUrl, id: videoId, duration } = {},
    } = item;

    if (contentPlatform !== ContentPlatforms.foxy) {
      return null;
    }

    const styles = playlistStyle;
    const play = shouldPlay && screenInFocus;
    const mixWithOthers = AudioHandler.isMuted ? 'mix' : 'inherit';
    const url = videoUrl.replace(VIDEO_DISTRIBUTION_URL, VIDEO_CDN_URL);
    let cardStyle = styles.verticalPlaylistPlayerStyle;
    if (edge_to_edge_playlist === '1' && !isDesktop()) {
      cardStyle = styles.edgeToEdgeVideoPlayerStyleInSquareCard;
    }

    return (
      <View style={styles.verticalCardMediaContainer}>
        <MediaComponent
          videoId={videoId}
          videoUrl={url}
          contentPlatform={contentPlatform}
          duration={duration}
          videoMetadata={videoMetadata}
          playerStyle={cardStyle}
          playerContainerStyle={mediaStyle}
          paused={!play}
          imageUrl={imageUrl}
          currentPage={play}
          reportBuffering={setIsBuffering}
          setCurrentTime={setCurrentTime}
          onLoadVideo={() => {}}
          onNextButtonTapped={onNextButtonTapped}
          onNextButtonClick={() => {}}
          navigation={navigation}
          muted={AudioHandler.isMuted}
          isVerticalCard={true}
          mixWithOthers={mixWithOthers}
          seekToTimeOnLoad={currentTime.current}
          isLastVideo={iteratorIndex === totalNumberOfVideos - 1}
        />
      </View>
    );
  }, [shouldPlay, screenInFocus, item, videoMetadata, mediaStyle, onNextButtonTapped, setCurrentTime, navigation, iteratorIndex, totalNumberOfVideos, edge_to_edge_playlist]);

  const MediaDescription = useCallback(({ artistName, artistImage, title, rating }) => {
    const styles = MediaCardStyles;
    const descriptionStyle = styles.verticalCardMediaDescriptionContainer;
    const gradientArray = ['rgba(0, 0, 0, 0.75)', 'rgba(0, 0, 0, 0)'];
    return (
      <View style={descriptionStyle}>
        <LinearGradient
          colors={gradientArray}
          style={styles.verticalPlaylistCardGradientStyle}
        >
          <View style={styles.verticalCardArtistContainer}>
            <FastImageView
              resizeMode="cover"
              source={artistImage.uri}
              style={styles.verticalCardArtistMiniAvatar}
            />
            <View style={styles.verticalPlaylistCardArtistDetails}>
              <Text
                numberOfLines={MAX_LINES.single}
                style={styles.verticalPlaylistCardTitle}
              >
                {title}
              </Text>
              <Text
                numberOfLines={MAX_LINES.single}
                style={styles.verticalPlaylistCardArtistName}
              >
                {artistName}
              </Text>
              <View style={styles.ratingStars}>
                <ProductRatingAndMoreInfo rating={rating} disableRatingText />
              </View>
            </View>
          </View>
        </LinearGradient>
      </View>
    );
  }, []);

  const {
    metadata: { title } = {},
    rating = 0,
  } = item;
  const { entity: { image_url: imageUrl } = {} } = item;

  const entityImage = Utility.isBlank(imageUrl)
    ? images.profilePlaceholder
    : {
        uri: getMinifiedImage(
          imageUrl,
          MediaCardStyles.verticalCardArtistMiniAvatar.width,
          MediaCardStyles.verticalCardArtistMiniAvatar.height,
        ),
      };

  const playStatus = shouldPlay && screenInFocus;
  let cardStyle = playlistStyle.oneRatioOneCardStyle;
  if (edge_to_edge_playlist === '1' && !isDesktop()) {
    cardStyle = playlistStyle.edgeToEdgeInSquareCardStyle;
  }

  return (
    <ScaleAnimate {...props} onPress={debouncedCard}>
      <Card style={cardStyle}>
        <VerticalMediaBgImage
          showBgImage={showBgImage || !isCurrentIndex}
          imageUrl={item.image_url}
          cardWidth={cardWidth}
        />
        {renderMedia()}
        <VerticalMediaImage
          isBuffering={isBuffering || contentPlatform !== ContentPlatforms.foxy}
          showImage={!shouldPlay}
          imageUrl={item.image_url}
          cardWidth={cardWidth}
        />
        <MediaDescription
          artistName={entityName}
          artistImage={entityImage}
          title={title}
          rating={rating}
        />
        {playStatus && contentPlatform === ContentPlatforms.foxy ? null : (
          <View style={playlistStyle.verticalCardMiddleControlsContainer}>
            <FastImageView source={images.playCircle} style={playlistStyle.verticalCardMiddleControls} />
          </View>
        )}
        {isBuffering ? (
          <View style={playlistStyle.verticalCardMiddleControlsContainer}>
            {Utility.getActivityIndicatorSmall(playlistStyle.verticalCardMiddleControls)}
          </View>
        ) : null}
        {!isBuffering && contentPlatform === ContentPlatforms.foxy ? (
          <View style={playlistStyle.verticalCardFullScreenButtonContainer}>
            <TouchableWithoutFeedback
              hitSlop={Utility.getHitSlop()}
              onPress={toggleMutedState}
            >
              <FastImageView
                source={
                  AudioHandler.isMuted
                    ? images.volumeMuteButton
                    : images.volumeUnmuteButton
                }
                style={playlistStyle.verticalPlaylistCardMuteButton}
              />
            </TouchableWithoutFeedback>
            <TouchableWithoutFeedback onPress={debouncedFullScreenButton}>
              <View style={playlistStyle.verticalPlaylistCardFullScreenButtonContainer}>
                <FastImageView
                  source={images.fullScreenMaximize}
                  style={playlistStyle.verticalPlaylistCardFullScreenButton}
                />
              </View>
            </TouchableWithoutFeedback>
          </View>
        ) : null}
      </Card>
    </ScaleAnimate>
  );
});

export default withNavigation(SquareMediaCard);